import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AccessForSelectFragment,
  PerformanceProcessPersonalResultsEvaluationsDocument,
  ReplaceReviewerPerformanceReviewEvaluationDocument,
} from 'generated/resources/graphql'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  onClose: () => void
  evaluationId: number
  excludedAccessIds: Set<number>
}

export const ReviewerSelectorModal = ({ onClose, evaluationId, excludedAccessIds }: Props) => {
  const [selectedAccess, setSelectedAccess] = React.useState<AccessForSelectFragment | null>(null)

  const [replaceReviewerEmployee, { loading: replacing }] = useMutation(
    ReplaceReviewerPerformanceReviewEvaluationDocument,
    {
      extractorFn: (data) => data.performanceMutations.replaceReviewerPerformanceReviewEvaluation,
      refetchQueries: [PerformanceProcessPersonalResultsEvaluationsDocument],
    }
  )

  const handleReviewerChange = async () => {
    if (!selectedAccess) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
      return
    }

    const response = await replaceReviewerEmployee({
      variables: {
        newReviewerAccessId: String(selectedAccess.id),
        evaluationId: String(evaluationId),
      },
    })

    const result = handleResult(response, (errors) => {
      if (errors.messages) {
        app.setFlash({
          type: 'wrong',
          message: errors.messages,
        })
      }
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    onClose()
  }

  return (
    <Modal
      illustration="user-male-female"
      title={i18n.t('performance_process.reviews.evaluations.replace_reviewer')}
      description={i18n.t('performance_process.reviews.evaluations.replace_reviewer_description')}
      onClose={onClose}
      onSubmit={handleReviewerChange}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!selectedAccess || replacing} submit>
            {i18n.t('common.save')}
          </Button>
        </>
      }
      disableFocusTrap
    >
      <AccessSelector
        mandatory
        withAvatar
        width="s400"
        value={selectedAccess}
        multiple={false}
        onSelect={(value: AccessForSelectFragment | null) => {
          setSelectedAccess(value)
        }}
        emptyLabel={i18n.t(
          'performance_process.reviews.basic_information_section.owners.empty_results'
        )}
        excludeIds={Array.from(excludedAccessIds)}
        withRegularAccess
        terminated={false}
      />
    </Modal>
  )
}
